import React, { useState, useEffect, useCallback } from 'react';
import { 
    Table, 
    Tag, 
    Button, 
    Space, 
    Typography, 
    message, 
    Tooltip, 
    Select, 
    Card, 
    Row, 
    Col 
} from 'antd';
import { 
    CheckOutlined, 
    CloseOutlined, 
    FilterOutlined, 
    ReloadOutlined,
    InfoCircleOutlined 
} from '@ant-design/icons';
import axiosInstance from '../../services/axiosServicesVisit';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const SuggestionRapportsPage = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const [filterField, setFilterField] = useState(null);
    const [filterDelegate, setFilterDelegate] = useState(null);
    const [delegatesList, setDelegatesList] = useState([]);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterField) params.fieldName = filterField;
            if (filterDelegate) params.delegateId = filterDelegate;

            const response = await axiosInstance.get('/v1/data-corrections/pending/reports', { params });
            setRequests(response.data);

            if (response.data.length > 0) {
                const uniqueDelegates = [];
                const map = new Map();
                for (const item of response.data) {
                    if(!map.has(item.submittedBy.id)){
                        map.set(item.submittedBy.id, true);
                        uniqueDelegates.push({
                            id: item.submittedBy.id,
                            name: `${item.submittedBy.firstname} ${item.submittedBy.lastname}`
                        });
                    }
                }
                setDelegatesList(prev => prev.length === 0 ? uniqueDelegates : prev);
            }
        } catch (error) {
            message.error("Erreur lors du chargement des suggestions.");
        } finally {
            setLoading(false);
        }
    }, [filterField, filterDelegate]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleResetFilters = () => {
        setFilterField(null);
        setFilterDelegate(null);
    };

    const handleAction = async (id, action) => {
        setActionLoading(id);
        try {
            await axiosInstance.post(`/v1/data-corrections/${id}/${action}`);
            setRequests(prevRequests => prevRequests.filter(req => req.id !== id));
            message.success(`Suggestion ${action === 'approve' ? 'approuvée' : 'rejetée'} avec succès.`);
        } catch (error) {
            message.error("Erreur lors du traitement.");
        } finally {
            setActionLoading(null);
        }
    };
    
    const columns = [
        {
            title: 'Date Soumission',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
            render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
        },
        {
            title: 'Délégué',
            key: 'delegate',
            width: 150,
            render: (_, record) => <Text strong>{`${record.submittedBy.firstname} ${record.submittedBy.lastname}`}</Text>,
        },
        {
            title: 'Rapport (Médecin & Date)',
            key: 'report',
            width: 200,
            render: (_, record) => (
                <div style={{ fontSize: '12px' }}>
                    <Text underline style={{ color: '#1890ff' }}>{`${record.report.person.firstname} ${record.report.person.lastname}`}</Text>
                    <br />
                    <Text type="secondary">{dayjs(record.report.date).format('DD/MM/YYYY')}</Text>
                </div>
            ),
        },
        {
            title: 'Champ Corrigé',
            dataIndex: 'fieldName',
            key: 'fieldName',
            width: 160,
            render: (field) => {
                let color = "blue";
                if (field === 'products_presented') color = 'orange';
                if (field === 'samples_given') color = 'green';
                return <Tag color={color}>{field.replace(/_/g, ' ').toUpperCase()}</Tag>;
            },
        },
        {
            title: 'Valeur Actuelle',
            dataIndex: 'currentValue',
            key: 'currentValue',
            width: 250, // Largeur fixe
            render: (value) => (
                <div style={{ 
                    whiteSpace: 'normal', 
                    wordBreak: 'break-word', 
                    maxWidth: '250px',
                    color: '#ff4d4f',
                    textDecoration: 'line-through' 
                }}>
                    {value || 'N/A'}
                </div>
            ),
        },
        {
            title: 'Valeur Suggérée',
            dataIndex: 'suggestedValue',
            key: 'suggestedValue',
            width: 250, // Largeur fixe
            render: (value) => (
                <div style={{ 
                    whiteSpace: 'normal', 
                    wordBreak: 'break-word', 
                    maxWidth: '250px',
                    color: '#52c41a',
                    fontWeight: 'bold' 
                }}>
                    {value}
                </div>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 240, // On définit une largeur fixe minimale
            render: (_, record) => (
                /* wrap={false} empêche les boutons de passer à la ligne */
                <Space size="small" wrap={false}> 
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => handleAction(record.id, 'approve')}
                        loading={actionLoading === record.id}
                        ghost
                        size="small"
                        style={{ minWidth: '100px' }} // Largeur minimale pour le bouton
                    >
                        Approuver
                    </Button>
                    <Button
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => handleAction(record.id, 'reject')}
                        loading={actionLoading === record.id}
                        size="small"
                        style={{ minWidth: '100px' }} // Largeur minimale pour le bouton
                    >
                        Rejeter
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Title level={3}>Suggestions de Correction des Rapports</Title>

            <Card style={{ marginBottom: 20, borderRadius: '8px' }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col><Text strong><FilterOutlined /> Filtres :</Text></Col>
                    <Col span={6}>
                        <Select
                            placeholder="Filtrer par champ"
                            style={{ width: '100%' }}
                            allowClear
                            value={filterField}
                            onChange={(val) => setFilterField(val)}
                        >
                            <Option value="date">Date de rapport</Option>
                            <Option value="products_presented">Produits présentés</Option>
                            <Option value="samples_given">Échantillons donnés</Option>
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Select
                            placeholder="Filtrer par délégué"
                            style={{ width: '100%' }}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            value={filterDelegate}
                            onChange={(val) => setFilterDelegate(val)}
                        >
                            {delegatesList.map(d => (
                                <Option key={d.id} value={d.id}>{d.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Button onClick={handleResetFilters} icon={<ReloadOutlined />}>Réinitialiser</Button>
                    </Col>
                </Row>
            </Card>

            <Table
                columns={columns}
                dataSource={requests}
                loading={loading}
                rowKey="id"
                bordered
                size="middle"
                
                expandable={{
                    expandedRowRender: (record) => (
                        <div style={{ padding: '12px 40px', background: '#fafafa', borderLeft: '4px solid #1890ff' }}>
                            <Text strong><InfoCircleOutlined /> Commentaire du délégué : </Text>
                            <Text>{record.comment || "Aucun commentaire."}</Text>
                        </div>
                    ),
                }}
                // scroll x: 'max-content' permet à la table de s'étendre si nécessaire
                scroll={{ x: 'max-content' }} 
                pagination={{ pageSize: 10 }}
            />
        </div>
    );
};

export default SuggestionRapportsPage;